import DeveloperIcon from '@assets/icons/developers.svg?react'
import LogoutIcon from '@assets/icons/logout.svg?react'
import MoonIcon from '@assets/icons/moon.svg?react'
import SettingsIcon from '@assets/icons/settings.svg?react'
import UserIcon from '@assets/icons/user.svg?react'
import { CoreProfileAvatar, MMButton, MMSwitchInput } from '@components'
import { ThemeContext } from '@context/ThemeContext'
import cn from 'classnames'
import {
  isOwnerOrAdministrator,
  MerchantEntityTypes,
  paths,
  useLogoutQry,
  useMerchantQry,
  useUserProfileQry
} from 'mmfintech-backend-api'
import { tr, useDropDownMenu } from 'mmfintech-commons'
import { type ComponentType, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { CompanySwitcher } from './CompanySwitcher'
import styles from './ProfileMenu.module.scss'
import { useCompanySwitch } from './useCompanySwitch'

type ProfileMenuItem = {
  key: string
  icon: ComponentType
  label: string
  path: string
  visible: boolean
}

export const ProfileMenu = () => {
  const history = useHistory()

  const { darkTheme, toggleTheme } = useContext(ThemeContext)

  const { visible, hideDropDown, toggleDropDown, setRef } = useDropDownMenu()

  const { merchant } = useMerchantQry()
  const { profile } = useUserProfileQry()
  const { logout } = useLogoutQry(true)
  const { companies, canSwitchCompany, currentMerchantId, parentMerchantId, switchCompany } = useCompanySwitch()

  useEffect(() => {
    if (!visible) {
      return
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideDropDown()
      }
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [visible, hideDropDown])

  if (!merchant) {
    return null
  }

  const { name: merchantName, apiEnabled, entityType, parentMerchantId: activeParentId } = merchant
  const isSubMerchant = Boolean(activeParentId)
  const { customerRole, firstName, lastName } = profile || {}

  const userName = `${firstName ?? ''} ${lastName ?? ''}`.trim()
  const hasAdminAccess = isOwnerOrAdministrator(customerRole)

  const items: ProfileMenuItem[] = [
    {
      key: 'profile',
      icon: SettingsIcon,
      label: tr('FRONTEND.HEADER.PROFILE_SETTINGS', 'Profile settings'),
      path: paths.profile(),
      visible: true
    },
    {
      key: 'user-management',
      icon: UserIcon,
      label: tr('FRONTEND.HEADER.USER_MANAGEMENT', 'User management'),
      path: paths.userDepartmentManagement(),
      visible: hasAdminAccess && entityType === MerchantEntityTypes.COMPANY
    },
    {
      key: 'developers',
      icon: DeveloperIcon,
      label: tr('FRONTEND.HEADER.DEVELOPERS', 'Developers'),
      path: paths.developer(),
      visible: Boolean(apiEnabled) && hasAdminAccess
    }
  ]

  const handleNavigate = (path: string) => {
    hideDropDown()
    history.push(path)
  }

  const handleSwitchCompany = (targetMerchantId: number) => {
    hideDropDown()
    void switchCompany(targetMerchantId)
  }

  const handleLogout = () => {
    hideDropDown()
    void logout()
  }

  return (
    <div ref={setRef} className={cn(styles['profile-menu'], 'profile-menu')}>
      <MMButton
        type='button'
        size='large'
        variant='elevated'
        className='profile-menu-trigger'
        collapsed
        collapsedIcon={<CoreProfileAvatar name={merchantName} />}
        onClick={toggleDropDown}
        title={tr('FRONTEND.PROFILE_MENU.TITLE', 'Account menu')}
        aria-haspopup='true'
        aria-expanded={visible}
        data-test='profile-menu-trigger'
      />

      {visible && (
        <div className={cn(styles['profile-menu-panel'], 'profile-menu-panel')} data-test='profile-menu-panel'>
          <div className={cn(styles['profile-menu-header'], 'profile-menu-header')}>
            <CoreProfileAvatar name={merchantName} />
            <div className={cn(styles['profile-menu-header-text'], 'profile-menu-header-text')}>
              <span className={cn(styles['profile-menu-company-title'], 'profile-menu-company-title')}>
                {merchantName}
              </span>
              {userName && <span className={cn(styles['profile-menu-user'], 'profile-menu-user')}>{userName}</span>}
              {(isSubMerchant || customerRole) && (
                <span className={cn(styles['profile-menu-role'], 'profile-menu-role')} data-test='profile-menu-role'>
                  {isSubMerchant
                    ? tr('FRONTEND.PROFILE_MENU.SUB_MERCHANT', 'Sub-merchant')
                    : tr(`METADATA.USER_ROLE.${customerRole}`, customerRole?.toLowerCase())}
                </span>
              )}
            </div>
          </div>

          {canSwitchCompany && (
            <CompanySwitcher
              companies={companies}
              currentMerchantId={currentMerchantId}
              parentMerchantId={parentMerchantId}
              onSelect={handleSwitchCompany}
            />
          )}

          <div className={cn(styles['profile-menu-list'], 'profile-menu-list')}>
            {items
              .filter(({ visible: isVisible }) => isVisible)
              .map(({ key, icon: Icon, label, path }) => (
                <MMButton
                  key={key}
                  type='button'
                  variant='menu-item'
                  fullWidth
                  leftIcon={<Icon />}
                  caption={label}
                  onClick={() => handleNavigate(path)}
                  data-test={`profile-menu-item-${key}`}
                />
              ))}

            {/* biome-ignore lint/a11y/noLabelWithoutControl: the nested MMSwitchInput renders the label's checkbox */}
            <label
              className={cn(styles['profile-menu-toggle-row'], 'profile-menu-toggle-row')}
              data-test='profile-menu-item-theme'
            >
              <MoonIcon />
              <span>{tr('FRONTEND.HAMBURGER_MENU.DARK_MODE', 'Dark mode')}</span>
              <MMSwitchInput
                className={cn(styles['profile-menu-switch'], 'profile-menu-switch')}
                checked={darkTheme}
                onChange={toggleTheme}
                aria-label={tr('FRONTEND.HAMBURGER_MENU.DARK_MODE', 'Dark mode')}
                data-test='profile-menu-theme-switch'
              />
            </label>
          </div>

          <div
            className={cn(
              styles['profile-menu-list'],
              'profile-menu-list',
              styles['profile-menu-list-footer'],
              'profile-menu-list-footer'
            )}
          >
            <MMButton
              type='button'
              variant='menu-item'
              className='mm-button-danger'
              fullWidth
              leftIcon={<LogoutIcon />}
              caption={tr('FRONTEND.HAMBURGER_MENU.LOG_OUT', 'Log out')}
              onClick={handleLogout}
              data-test='profile-menu-item-logout'
            />
          </div>
        </div>
      )}
    </div>
  )
}
